import type { Faire } from '@/content/renaissance-faires/types'

/**
 * Faire schedule maths.
 *
 * Every function here takes "today" as an explicit `YYYY-MM-DD` string and
 * compares dates as plain `YYYYMMDD` integers — no `Date`, no `Date.now()`, no
 * timezone. That is what keeps the rendered date lines a pure function of the
 * server's anchor (`invariants.md#ssr-safe-rendered-dates`): the finder is a
 * client island, so an ambient-clock read here would produce a different
 * "Open now" on the server than in the browser and hydrate mismatched.
 */

/** How a faire's next run reads relative to today. */
export type ScheduleTone =
    /** Gates are open right now. */
    | 'running'
    /** Dated and still ahead. */
    | 'upcoming'
    /** This year's run has passed; next year's is projected from the month. */
    | 'projected'
    /** Only the month is known, or nothing is. */
    | 'unposted'

export interface Schedule {
    /** Rendered line, e.g. "Open now — Aug 22 – Oct 4". */
    dateLine: string
    tone: ScheduleTone
    /** `YYYYMMDD`-shaped sort key: soonest first. */
    sortKey: number
}

const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function daysInMonth(year: number, month: number): number {
    return month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]
}

/** `YYYY-MM-DD` / `YYYY-MM` → a comparable `YYYYMMDD` integer. */
function toOrdinal(iso: string, monthPrecisionAs: 'start' | 'end'): number {
    const year = Number(iso.slice(0, 4))
    const month = Number(iso.slice(5, 7))
    if (iso.length === 7) {
        const day = monthPrecisionAs === 'start' ? 1 : daysInMonth(year, month)
        return year * 10000 + month * 100 + day
    }
    return year * 10000 + month * 100 + Number(iso.slice(8, 10))
}

/** True when `start` carries only a month — the dataset's "dates not posted" shape. */
function isMonthPrecision(start: string): boolean {
    return start.length === 7
}

/**
 * Add a year to a dates line that doesn't name one.
 *
 * Most month-precision rows end in a hedge — "Jan/Feb (TBD)", "Late Sep / early
 * Oct (TBA)" — so a bare append would read "Jan/Feb (TBD) (2027)". The year goes
 * *before* a trailing parenthetical and into its own parens otherwise, which
 * gives "Jan/Feb 2027 (TBD)" and "Feb 7–8 (2027)" from the one rule.
 */
function withYear(text: string, year: number): string {
    const trailingParenthetical = /\s*\([^()]*\)$/.exec(text)
    if (!trailingParenthetical) {
        return `${text} (${year})`
    }
    const head = text.slice(0, trailingParenthetical.index)
    return `${head} ${year}${trailingParenthetical[0]}`
}

/**
 * Where a faire's next run sits relative to `today` (`YYYY-MM-DD`).
 *
 * A faire whose run has already passed gets next year's edition projected from
 * this year's start month — stated as a projection ("Next: ~Sep 2027 — dates
 * TBD"), never as though the faire had announced it.
 */
export function scheduleOf(faire: Faire, today: string): Schedule {
    const now = toOrdinal(today, 'start')
    const text = faire.datesText

    if (!faire.start) {
        // Nothing dated at all: sort to the very back, behind every projection.
        const year = Number(today.slice(0, 4)) + 2
        return { dateLine: text, tone: 'unposted', sortKey: year * 10000 + 1231 }
    }

    const monthOnly = isMonthPrecision(faire.start)
    const start = toOrdinal(faire.start, 'start')
    const end = faire.end ? toOrdinal(faire.end, 'end') : toOrdinal(faire.start, 'end')

    if (!monthOnly && start <= now && end >= now) {
        return { dateLine: `Open now — ${text}`, tone: 'running', sortKey: now }
    }

    if (start > now) {
        const startYear = Number(faire.start.slice(0, 4))
        // Disambiguate a run in a later year when the dates text doesn't name it.
        const needsYear = startYear > Number(today.slice(0, 4)) && !text.includes(String(startYear))
        return {
            dateLine: needsYear ? withYear(text, startYear) : text,
            tone: monthOnly ? 'unposted' : 'upcoming',
            // Month-precision rows sort behind dated rows in the same window.
            sortKey: monthOnly ? end : start,
        }
    }

    if (monthOnly && end >= now) {
        return { dateLine: text, tone: 'unposted', sortKey: end }
    }

    // Passed: project next year's edition from this year's start month.
    const year = Number(faire.start.slice(0, 4)) + 1
    const month = Number(faire.start.slice(5, 7))
    return {
        dateLine: `Next: ~${MONTH_NAMES[month - 1]} ${year} — dates TBD. This year ran ${text}`,
        tone: 'projected',
        sortKey: year * 10000 + month * 100 + Number(faire.start.slice(8, 10) || '01'),
    }
}

/** A faire with its schedule resolved, so a list sorts and renders from one pass. */
export interface ScheduledFaire extends Faire {
    schedule: Schedule
}

export function withSchedules(list: Faire[], today: string): ScheduledFaire[] {
    return list.map((faire) => ({ ...faire, schedule: scheduleOf(faire, today) }))
}

/**
 * The next faires to open the gates: open now, or dated and ahead. Sorted
 * soonest-first and capped, for the "Coming up next" rail.
 */
export function comingUpNext(
    list: Faire[],
    today: string,
    limit = 8,
): { faire: ScheduledFaire; when: string; isOpen: boolean }[] {
    return withSchedules(list, today)
        .filter((f) => f.schedule.tone === 'running' || f.schedule.tone === 'upcoming')
        .sort((a, b) => a.schedule.sortKey - b.schedule.sortKey)
        .slice(0, limit)
        .map((faire) => {
            const isOpen = faire.schedule.tone === 'running'
            const month = Number(faire.start?.slice(5, 7) ?? '1')
            const day = Number(faire.start?.slice(8, 10) ?? '1')
            return {
                faire,
                isOpen,
                when: isOpen ? 'Open now' : `Opens ${MONTH_NAMES[month - 1]} ${day}`,
            }
        })
}
